import React from "react";
import {Box, SimpleGrid, Flex, Text, Icon, Badge, Divider} from "@chakra-ui/react";
import {LuCalendarRange} from "react-icons/lu";
import {HiOutlineDocumentDuplicate} from "react-icons/hi";
import {MdOutlineAttachMoney} from "react-icons/md";
import {useNavigate, useLocation} from "react-router-dom";
import {fetchDependentOverview} from "../../api/dependents";
import {useDependentSection, SECTION_STATES} from "./useDependentSection";
import DependentShell from "./DependentShell";
import {formatDateTime, formatDate} from "./format";
import {formatMoneyCents} from "../ui/Money";
import {SharedNotesList} from "../SharedNotes";

// The guardian's home screen for a dependent.
//
// One request, composed server-side. The alternative — five requests assembled
// in the browser — puts the client in charge of deciding what a home screen may
// show, and a composition is exactly where individually harmless fragments add
// up to a disclosure.
//
// Every card here is present only because the server sent that key. A guardian
// without billing access does not see a balance of zero; they see no balance
// card at all, because "nothing owed" is still an answer about somebody else's
// money.

export default function DependentHome({state}) {
  const navigate = useNavigate();
  const location = useLocation();
  const section = useDependentSection({
    state,
    section: "overview",
    load: fetchDependentOverview,
  });

  const data = section.data;
  const go = (to) => navigate(to, {state: location.state});

  return (
    <DependentShell
      title={section.displayName}
      subtitle="What you can see and do for this person"
      displayName={section.displayName}
      accessLevel={section.dependent?.accessLevel}
      status={section.status}
      error={section.error}
      onRetry={section.reload}
      onLeave={section.leave}
    >
      {section.status === SECTION_STATES.READY && data && (
        <>
          <IdentityCard identity={data.identity} ageBand={data.ageBand} capped={data.capped} />

          <SimpleGrid columns={{base: 1, md: data.balance ? 3 : 2}} spacing={4} mt={5}>
            {"nextAppointment" in data && (
              <Card
                icon={LuCalendarRange}
                label="Next appointment"
                onClick={() => go("../appointments")}
                // The clinic's zone, not the reader's. This card and the
                // appointments list name the SAME appointment, and without the
                // zone this one rendered in the browser's: a guardian two zones
                // away read "4:15 p.m." here and "11:15 a.m." one click later.
                // An hour that disagrees with itself is worse than no hour.
                value={
                  data.nextAppointment
                    ? formatDateTime(data.nextAppointment.startsAt, data.timeZone)
                    : "None booked"
                }
                note={
                  data.nextAppointment
                    ? [data.nextAppointment.serviceName, data.nextAppointment.clinicianName]
                        .filter(Boolean)
                        .join(" · ")
                    : "Nothing upcoming that you can see"
                }
              />
            )}

            {"formsOutstanding" in data && (
              <Card
                icon={HiOutlineDocumentDuplicate}
                label="Forms to complete"
                onClick={() => go("../forms")}
                value={String(data.formsOutstanding)}
                note={
                  data.formsOutstanding === 0
                    ? "Nothing waiting"
                    : "Waiting to be filled in"
                }
              />
            )}

            {data.balance && (
              <Card
                icon={MdOutlineAttachMoney}
                label="Outstanding balance"
                onClick={() => go("../account")}
                value={formatMoneyCents(data.balance.totals?.outstandingCents)}
                note={
                  data.balance.outstandingCount === 0
                    ? "Nothing owed"
                    : `${data.balance.outstandingCount} item${
                        data.balance.outstandingCount === 1 ? "" : "s"
                      }`
                }
              />
            )}
          </SimpleGrid>

          {/* One renderer for the guardian's copy and the account holder's own,
              so the same note is never described two different ways. The
              guardian's arrives inside the overview — composed server-side —
              rather than as a second request from the browser. */}
          <SharedNotesList
            notes={data.sharedNotes}
            heading={`From your clinic about ${section.displayName}`}
          />

          {/* Said where the hour is shown, not only on the list it links to.
              A time with no zone beside it is a claim the reader cannot check. */}
          {data.nextAppointment && data.timeZone && (
            <Text fontSize="xs" color="gray.500" mt={4}>
              Times are shown in your clinic's local time.
            </Text>
          )}

          <Text fontSize="xs" color="gray.500" mt={6}>
            Your clinic decides what you can see here. Clinical records — chart notes,
            prescriptions, journal entries and messages — are never shared through a guardian
            account, and nothing above is a substitute for them. What appears under "From your
            clinic" is there because somebody chose to show it to you.
          </Text>
        </>
      )}
    </DependentShell>
  );
}

// The age band is stated plainly rather than left to be inferred from a
// suddenly shorter menu. A guardian whose access narrowed on a birthday and was
// told nothing concludes the app is broken.
const BAND_NOTE = {
  child: null,
  assent: "This person is now old enough to be asked about sharing their own information.",
  capacity:
    "This person is now old enough to make their own decisions about parts of their care, so some information is no longer shared with guardians.",
  adult:
    "This person has reached the age of majority. Guardian access is paused and your clinic must review it before it can continue.",
};

function IdentityCard({identity, ageBand, capped}) {
  const note = BAND_NOTE[ageBand];
  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="12px" p={5} bg="white">
      <Flex gap={6} wrap="wrap">
        <Field label="Name" value={`${identity?.fName || ""} ${identity?.lName || ""}`.trim()} />
        <Field label="Date of birth" value={formatDate(identity?.dob || identity?.dateOfBirth)} />
      </Flex>
      {(note || capped) && (
        <>
          <Divider my={4} />
          <Flex align="flex-start" gap={2}>
            {capped && (
              <Badge colorScheme="orange" fontSize="10px" textTransform="none" flexShrink={0}>
                Access limited
              </Badge>
            )}
            <Text fontSize="sm" color="gray.700">
              {note ||
                "Some of what your clinic granted you is currently limited. Please contact them if you need more."}
            </Text>
          </Flex>
        </>
      )}
    </Box>
  );
}

function Field({label, value}) {
  return (
    <Box>
      <Text fontSize="11px" letterSpacing="0.04em" textTransform="uppercase" color="gray.500">
        {label}
      </Text>
      <Text fontSize="15px" fontWeight="600" color="#1a1f36">
        {value || "—"}
      </Text>
    </Box>
  );
}

function Card({icon, label, value, note, onClick}) {
  return (
    <Box
      as="button"
      textAlign="left"
      onClick={onClick}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="12px"
      p={5}
      bg="white"
      transition="all 0.15s ease"
      _hover={{borderColor: "orange.300", transform: "translateY(-1px)"}}
    >
      <Flex align="center" gap={2} mb={3}>
        <Icon as={icon} color="orange.500" />
        <Text fontSize="12px" fontWeight="600" color="gray.600">
          {label}
        </Text>
      </Flex>
      <Text fontSize="20px" fontWeight="700" color="#1a1f36" lineHeight="1.2">
        {value}
      </Text>
      <Text fontSize="xs" color="gray.500" mt={1} noOfLines={2}>
        {note}
      </Text>
    </Box>
  );
}
